import type { PlayerProfileResponse } from '../../../../../services/player/playerProfile'
import { mapPlayerToForm } from '../../mapPlayerToForm'
import {
  ADMIN_BOOLEAN_FIELDS,
  ADMIN_FIELD_TO_BE_KEY,
  initialPlayerDetailsValues,
} from '../constants'
import type {
  PlayerDetailsAdminField,
  PlayerDetailsAdminValues,
  PlayerDetailsFormValues,
} from '../types'

const toText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/** Date-valued admin fields arrive as ISO; the form keeps `YYYY-MM-DD`. */
const toDateOnly = (value: unknown): string => {
  const text = toText(value)
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text
}

const DATE_FIELDS: ReadonlySet<PlayerDetailsAdminField> =
  new Set<PlayerDetailsAdminField>(['blacklistedAt'])

/**
 * Read the given admin fields off a player response, keyed by form field:
 * switches as booleans, dates as `YYYY-MM-DD`, everything else as text ('' when
 * the API sends null or omits it).
 */
export const mapAdminFieldsFromPlayer = (
  player: PlayerProfileResponse,
  fields: ReadonlyArray<PlayerDetailsAdminField>
): Partial<PlayerDetailsAdminValues> =>
  fields.reduce<Partial<PlayerDetailsAdminValues>>((acc, field) => {
    const raw = player[ADMIN_FIELD_TO_BE_KEY[field]]
    if (ADMIN_BOOLEAN_FIELDS.has(field)) {
      return { ...acc, [field]: Boolean(raw) }
    }
    if (DATE_FIELDS.has(field)) {
      return { ...acc, [field]: toDateOnly(raw) }
    }
    return { ...acc, [field]: toText(raw) }
  }, {})

/**
 * GET /players/:id → Player Details form values. The Edit Player fields come
 * from the shared `mapPlayerToForm`; the admin fields are read from the keys
 * in `ADMIN_FIELD_TO_BE_KEY`, defaulting when the API omits them.
 */
export const mapPlayerDetailsToForm = (
  player: PlayerProfileResponse
): PlayerDetailsFormValues => {
  const adminFields = Object.keys(
    ADMIN_FIELD_TO_BE_KEY
  ) as PlayerDetailsAdminField[]

  const admin = mapAdminFieldsFromPlayer(player, adminFields)

  return { ...initialPlayerDetailsValues, ...mapPlayerToForm(player), ...admin }
}
